import { Injectable, computed, signal } from '@angular/core';

import type { CustomerProfileView } from '../models/customer-profile.types';
import type { PersistedCustomerAuth } from '../utils/customer-auth-token';
import {
  clearPersistedCustomerAuth,
  extractCustomerAccessToken,
  extractCustomerAvatarUrl,
  loadPersistedCustomerAuth,
  savePersistedCustomerAuth,
} from '../utils/customer-auth-token';

@Injectable({ providedIn: 'root' })
export class CustomerAuthSessionService {
  /** JWT or bearer string from verify-OTP; null if absent or cleared. */
  private readonly token = signal<string | null>(null);
  readonly accessToken = this.token.asReadonly();

  private readonly sessionEmailSignal = signal<string | null>(null);
  readonly sessionEmail = this.sessionEmailSignal.asReadonly();

  private readonly avatarUrlSignal = signal<string | null>(null);
  readonly avatarUrl = this.avatarUrlSignal.asReadonly();

  private readonly customerProfileSignal = signal<CustomerProfileView | null>(null);
  readonly customerProfile = this.customerProfileSignal.asReadonly();

  readonly isLoggedIn = computed(() => Boolean(this.token()?.trim()));

  constructor() {
    const saved = loadPersistedCustomerAuth();
    if (saved) {
      this.token.set(saved.token);
      this.sessionEmailSignal.set(saved.email ?? null);
      this.avatarUrlSignal.set(saved.avatarUrl?.trim() ? saved.avatarUrl : null);
      this.customerProfileSignal.set(saved.customerProfile ?? null);
    }
  }

  /** Call after successful `verifyOtp`; persists when a token exists. Clears cached profile until refreshed. */
  setSessionFromVerifyResponse(email: string, body: unknown): void {
    const extracted = extractCustomerAccessToken(body);
    const avatar = extractCustomerAvatarUrl(body);
    this.token.set(extracted);
    this.sessionEmailSignal.set(email.trim());
    this.avatarUrlSignal.set(avatar);
    this.customerProfileSignal.set(null);
    const state: PersistedCustomerAuth = {
      token: extracted ?? null,
      email: email.trim(),
      avatarUrl: avatar,
      customerProfile: null,
    };
    savePersistedCustomerAuth(state);
  }

  /** Saves full profile (e.g. after `GET /api/auth/customer/me`) to memory and localStorage. */
  setCustomerProfile(profile: CustomerProfileView): void {
    this.customerProfileSignal.set(profile);
    const thumb = profile.imgUrl?.trim() ? profile.imgUrl : this.avatarUrlSignal();
    if (thumb) {
      this.avatarUrlSignal.set(thumb);
    }
    this.persistCurrentAuth();
  }

  clearSession(): void {
    this.token.set(null);
    this.sessionEmailSignal.set(null);
    this.avatarUrlSignal.set(null);
    this.customerProfileSignal.set(null);
    clearPersistedCustomerAuth();
  }

  private persistCurrentAuth(): void {
    const state: PersistedCustomerAuth = {
      token: this.token(),
      email: this.sessionEmailSignal(),
      avatarUrl: this.avatarUrlSignal(),
      customerProfile: this.customerProfileSignal(),
    };
    savePersistedCustomerAuth(state);
  }
}
